import { useEffect, useMemo, useRef, useState } from 'react';
import {
  drawPage, fits, FONTS, heightSizePx, HEIGHTS, heightMm, loadFont, parseStickers, pxToPt, sizesPx, toPages, ROLL_WIDTH_MM,
  type Columns, type FontKey, type HeightKey, type Sticker,
} from '@/entities/price-sticker';
import { makeStickerPdf } from '@/features/price-sticker-pdf';
import { PrintHelpButton } from '@/features/print-help';
import { Field, Fold, Page, Toggle } from '@/shared/ui';
import { toast } from '@/shared/lib';
import { useStickerSettings } from '../model/settings';

const PREVIEW_SCALE = 0.35;   // the preview is drawn at about 210 dots per inch; the PDF at 600
const PREVIEW_MAX_PAGES = 60; // a long list previews its first pages only; the PDF always has every page
const round1 = (n: number) => Math.round(n * 10) / 10;

// One page of the preview: a canvas the drawing code paints, named for screen readers by the stickers on it.
function PreviewPage({ page, index, draw }: { page: Sticker[]; index: number; draw: (c: HTMLCanvasElement, p: Sticker[], i: number) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => { if (ref.current) draw(ref.current, page, index); }, [draw, page, index]);
  return (
    <figure className="strip-page">
      <canvas ref={ref} role="img" aria-label={`Page ${index + 1}: ${page.map((l) => l.join(' ')).join(', ')}`} />
      <figcaption>Page {index + 1}</figcaption>
    </figure>
  );
}

// Price stickers for the Brother label printer's 62 mm roll: settings on the left, a preview of every page (one piece of the
// roll per page) on the right, "How to print" and "Download PDF" in the top bar.
export function PriceStickerEditor() {
  const { s, set, reset } = useStickerSettings();
  const [text, setText] = useState('');
  const [fontReady, setFontReady] = useState(0);
  const stickers = useMemo(() => parseStickers(text), [text]);
  const pages = useMemo(() => toPages(stickers, s.columns), [stickers, s.columns]);

  // the chosen font must be loaded before anything is measured or drawn
  useEffect(() => {
    let live = true;
    loadFont(s.font, text).then(() => { if (live) setFontReady((n) => n + 1); });
    return () => { live = false; };
  }, [s.font, text]);

  // sizes: measured again once the font has loaded (fontReady)
  const autoPx = useMemo(() => heightSizePx(s), [s, fontReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const sizes = useMemo(() => sizesPx(stickers, s), [stickers, s, fontReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const pageSizes = useMemo(() => toPages(sizes.map((x) => [String(x)]), s.columns).map((p) => p.map((x) => Number(x[0]))), [sizes, s.columns]);
  const tooBig = useMemo(() => (s.autoSize ? [] : stickers.map((st, i) => (fits(st, s, sizes[i]!) ? -1 : i + 1)).filter((i) => i > 0)), [stickers, s, sizes]);

  const draw = useMemo(() => (canvas: HTMLCanvasElement, page: Sticker[], i: number) => drawPage(canvas, page, s, pageSizes[i]!, PREVIEW_SCALE), [s, pageSizes]);

  const [busy, setBusy] = useState(false);
  const download = async () => {
    if (!pages.length) { toast('Type at least one sticker first.', true); return; }
    setBusy(true);
    try {
      await loadFont(s.font, text);
      await makeStickerPdf(pages, s, pageSizes);
      toast(`PDF saved ✓ (${pages.length} page${pages.length > 1 ? 's' : ''})`);
    } catch (e) {
      console.error(e);
      toast('The PDF could not be made. Try again.', true);
    } finally { setBusy(false); }
  };

  const h = heightMm(s.height);
  const heightLabel = HEIGHTS.find((x) => x.key === s.height)!.label;
  const sizeNote = !s.autoSize ? `${s.sizePt} pt` : `Auto, ${round1(pxToPt(autoPx))} pt`;
  const shrunk = s.autoSize ? sizes.filter((x) => x < autoPx).length : 0;
  const shown = pages.slice(0, PREVIEW_MAX_PAGES);

  return (
    <Page title="Price Stickers" actions={<>
      <PrintHelpButton />
      <button type="button" className="btn" onClick={download} disabled={busy}>↓ Download PDF</button>
    </>}>
      <div className="editor">
        <div className="card side-panel">
          <Fold title="Roll Setup" note={`${s.columns === 1 ? '1 column' : '2 columns'} · ${heightLabel}`} open remember="stickers.roll">
            <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>Brother label printer, 62 mm continuous roll. Each PDF page is one piece of the roll.</p>
            <span className="choice-label" id="colsLabel">Columns</span>
            <div className="choices two" role="group" aria-labelledby="colsLabel">
              {([1, 2] as Columns[]).map((c) => (
                <button key={c} type="button" className="choice" aria-pressed={s.columns === c} onClick={() => set({ columns: c })}>
                  <span className="grid-viz" style={{ gridTemplateColumns: c === 2 ? '1fr 1fr' : '1fr' }} aria-hidden="true">
                    {Array.from({ length: c }, (_, i) => <span key={i} className="gc" style={{ width: c === 2 ? 11 : 24, height: 10 }} />)}
                  </span>
                  {c === 1 ? '1 column (full width)' : '2 columns'}
                </button>
              ))}
            </div>
            <span className="choice-label" id="heightLabel">Sticker height</span>
            <div className="choices" role="group" aria-labelledby="heightLabel">
              {HEIGHTS.map((x) => (
                <button key={x.key} type="button" className="choice compact" aria-pressed={s.height === x.key} onClick={() => set({ height: x.key as HeightKey })}>{x.label}</button>
              ))}
            </div>
            {s.columns === 2 && <Toggle label="Light dotted cut line between the columns" checked={s.cutLine} onChange={(cutLine) => set({ cutLine })} />}
          </Fold>

          <Fold title="Text Style" note={sizeNote} open remember="stickers.text">
            <Field label="Font">
              {(id) => (
                <select id={id} value={s.font} onChange={(e) => set({ font: e.target.value as FontKey })}>
                  {FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              )}
            </Field>
            <Toggle label="Size automatically (fills the sticker's height)" checked={s.autoSize}
              onChange={(autoSize) => set(autoSize ? { autoSize } : { autoSize, sizePt: Math.max(2, Math.round(pxToPt(autoPx) * 2) / 2) })} />
            <div className="grid2">
              <Field label="Font size (pt)" hint={s.autoSize ? `Auto for ${heightLabel}: ${round1(pxToPt(autoPx))} pt${shrunk ? `; ${shrunk} long sticker${shrunk > 1 ? 's are' : ' is'} smaller to fit` : ''}` : undefined}>
                {(id) => <input id={id} type="number" min={2} max={200} step={0.5} disabled={s.autoSize}
                  value={s.autoSize ? round1(pxToPt(autoPx)) : s.sizePt}
                  onChange={(e) => { const v = parseFloat(e.target.value); if (isFinite(v)) set({ sizePt: Math.min(200, Math.max(2, v)) }); }} />}
              </Field>
              <Field label="Letter spacing (pt)" hint="0 is normal; below 0 is tighter">
                {(id) => <input id={id} type="number" min={-5} max={20} step={0.1} value={s.spacingPt}
                  onChange={(e) => { const v = parseFloat(e.target.value); if (isFinite(v)) set({ spacingPt: Math.min(20, Math.max(-5, v)) }); }} />}
              </Field>
            </div>
            {tooBig.length > 0 && (
              <div className="sku-status warn" role="status">At {s.sizePt} pt, sticker{tooBig.length > 1 ? 's' : ''} {tooBig.join(', ')} {tooBig.length > 1 ? "don't" : "doesn't"} fit and will be cut off. Use a smaller size or turn Auto back on.</div>
            )}
            <button type="button" className="btn ghost sm" style={{ marginTop: 14 }} onClick={reset}>Reset roll and text settings</button>
          </Fold>

          <Fold title="Stickers" note={`${stickers.length} sticker${stickers.length === 1 ? '' : 's'} · ${pages.length} page${pages.length === 1 ? '' : 's'}`} open remember="stickers.list">
            <Field label="Stickers, one per line" hint={<>One sticker per line. Use <b>|</b> to put a second line on a sticker, e.g. <b>$8.49 | per lb</b>. Paste a column from a spreadsheet to make many at once.</>}>
              {(id) => <textarea id={id} rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={'$12.99\n$8.49 | per lb\n2 for $5'} />}
            </Field>
          </Fold>

          <div className="help-callout">
            <b>Printing on the Brother for the first time?</b>
            <span>Follow the step-by-step guide. Adobe Reader is the easiest way.</span>
            <PrintHelpButton className="btn ghost sm" label="How to print" />
          </div>
        </div>

        <section className="card editor-preview" aria-label="Preview">
          <div className="preview-head">
            <h3>Preview — {ROLL_WIDTH_MM} mm roll</h3>
            <span className="preview-meta">{stickers.length} sticker{stickers.length === 1 ? '' : 's'} · {pages.length} page{pages.length === 1 ? '' : 's'} · each page {ROLL_WIDTH_MM} × {h} mm</span>
          </div>
          <div className="strip-stage">
            {pages.length === 0
              ? <div className="empty">Type your prices under <b>Stickers</b>, one per line, and they show up here.</div>
              : <div className="strip">{shown.map((p, i) => <PreviewPage key={i} index={i} page={p} draw={draw} />)}</div>}
            {pages.length > PREVIEW_MAX_PAGES && <p className="hint" style={{ textAlign: 'center' }}>Showing the first {PREVIEW_MAX_PAGES} of {pages.length} pages. The PDF has all of them.</p>}
          </div>
        </section>
      </div>
    </Page>
  );
}
